import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { localCopy } from 'tracked-toolbox';

import type { IconName } from '../../types/components/prism/icon.ts';
import PrismIcon from './icon.gts';
import PrismTooltip from './tooltip.gts';

const INPUT_TYPES = {
  TEXT: 'text',
  PASSWORD: 'password',
  EMAIL: 'email',
  NUMBER: 'number',
  SEARCH: 'search',
} as const;

type InputType = (typeof INPUT_TYPES)[keyof typeof INPUT_TYPES];

export interface PrismInputSignature {
  Args: {
    autocomplete?: string;
    autofocus?: boolean;
    disabled?: boolean;
    errorMessage?: string;
    fillHeight?: boolean;
    hasErrors?: boolean;
    hasWarnings?: boolean;
    helpText?: string;
    iconAfter?: {
      name: IconName;
      onClick?: (event?: Event) => unknown;
    };
    inlineLabel?: string;
    inputmode?: string;
    isClearable?: boolean;
    isSmall?: boolean;
    isTouched?: boolean;
    label?: string;
    maxLength?: number;
    name?: string;
    onBlur?: (event: Event) => unknown;
    onClearInput?: () => unknown;
    onClick?: (event: Event) => unknown;
    onFocusIn?: (event: Event) => unknown;
    onFocusOut?: (event: Event) => unknown;
    onInput?: (event: Event) => unknown;
    onKeyDown?: (event: KeyboardEvent) => unknown;
    onKeyUp?: (event: KeyboardEvent) => unknown;
    placeholder?: string;
    readonly?: boolean;
    removePadding?: boolean;
    required?: boolean;
    requiredErrorMessage?: string;
    rows?: number;
    textarea?: boolean;
    tooltip?: string;
    type?: InputType;
    value?: string | number;
    warningMessage?: string;
  };
  Element: HTMLTextAreaElement | HTMLInputElement;
}

export default class PrismInput extends Component<PrismInputSignature> {
  @tracked isPasswordVisible = false;

  focusElement = modifier(
    (element: HTMLInputElement | HTMLTextAreaElement): void => {
      if (this.args.autofocus) {
        element.focus();
      }
    },
  );

  input: null | HTMLInputElement | HTMLTextAreaElement = null;

  @localCopy('args.isTouched') declare isTouched?: boolean;

  get hasErrorMessage(): boolean {
    if (!this.isInErrorState) {
      return false;
    }

    const { errorMessage, requiredErrorMessage } = this.args;

    if (errorMessage) {
      return true;
    }

    return this.showRequiredMessage && Boolean(requiredErrorMessage);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasErrors() {
    return this.isTouched && (this.showRequiredMessage || this.args.hasErrors);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasWarningMessage() {
    return Boolean(this.args.hasWarnings && this.args.warningMessage);
  }

  get iconAfter(): {
    name: IconName;
    onClick?: (event?: Event) => unknown;
  } | null {
    const { iconAfter, type } = this.args;

    if (type === INPUT_TYPES.PASSWORD) {
      return {
        name: this.isPasswordVisible ? 'visibility-off' : 'visibility-on',
        // eslint-disable-next-line @typescript-eslint/unbound-method
        onClick: this.toggleVisibility,
      };
    }

    if (iconAfter) {
      return iconAfter;
    }

    return null;
  }

  get isInErrorState(): boolean {
    if (this.hasErrors) {
      return true;
    }

    if (this.type === INPUT_TYPES.NUMBER) {
      return false;
    }

    return (
      this.args.maxLength !== undefined &&
      this.args.maxLength < this.value?.length
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get noPadding() {
    return (
      this.args.removePadding || this.hasErrorMessage || this.hasWarningMessage
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showClearButton() {
    return Boolean(this.args.isClearable && this.value);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showContentOptions() {
    return (
      this.args.inlineLabel ||
      this.iconAfter ||
      this.showClearButton ||
      this.isInErrorState ||
      this.args.maxLength
    );
  }

  get showRequiredMessage(): boolean {
    if (!this.args.required) {
      return false;
    }

    return !this.value;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get type() {
    const { type } = this.args;

    if (type === INPUT_TYPES.PASSWORD) {
      return this.isPasswordVisible ? INPUT_TYPES.TEXT : INPUT_TYPES.PASSWORD;
    }

    return type;
  }

  get value(): string {
    return String(this.args.value ?? '');
  }

  @action
  clearInput(): void {
    if (!this.input) {
      return;
    }
    this.input.value = '';
    this.input?.dispatchEvent(new Event('input', { bubbles: true }));
    this.args.onClearInput?.();
  }

  @action
  onBlur(event: Event): void {
    this.isTouched = true;
    this.args.onBlur?.(event);
  }

  @action
  onClick(event: Event): void {
    this.args.onClick?.(event);
  }

  @action
  onFocusIn(event: Event): void {
    this.args.onFocusIn?.(event);
  }

  @action
  onFocusOut(event: Event): void {
    this.args.onFocusOut?.(event);
  }

  @action
  onInput(event: Event): void {
    this.args.onInput?.(event);
  }

  @action
  onKeyDown(event: KeyboardEvent): void {
    this.args.onKeyDown?.(event);
  }

  @action
  onKeyUp(event: KeyboardEvent): void {
    this.args.onKeyUp?.(event);
  }

  @action
  setInput(element: HTMLInputElement | HTMLTextAreaElement): void {
    this.input = element;
  }

  @action
  toggleVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  <template>
    {{! template-lint-disable no-autofocus-attribute no-invalid-interactive require-presentational-children }}
    <div
      class="prism-input
        {{if @hasWarnings 'prism-input--warning'}}
        {{if this.isInErrorState 'prism-input--error'}}
        {{if this.noPadding 'prism-input--no-padding'}}
        {{if @disabled 'prism-input--disabled'}}
        {{if @readonly 'prism-input--disabled'}}
        {{if @fillHeight 'prism-input--fill-height'}}"
    >
      {{#if @label}}
        <label class="text-200--bold prism-input__label">
          {{@label}}
          {{#if @tooltip}}
            <span class="prism-input__tooltip">
              <PrismIcon
                @name="circle-information-outline"
                @size="extra-small"
              />
              <PrismTooltip @text={{@tooltip}} />
            </span>
          {{/if}}
          {{#if @required}}
            <span class="prism-input__label__required-indicator">
              *</span>
          {{/if}}
        </label>
      {{/if}}

      <div class="prism-input__wrapper" {{on "click" this.onClick}}>
        {{#if @textarea}}
          <textarea
            autocomplete={{@autocomplete}}
            autofocus={{if @autofocus "true"}}
            class={{if
              @isSmall
              "prism-input__textarea--small text-200--regular"
              "text-300--regular"
            }}
            disabled={{@disabled}}
            inputmode={{@inputmode}}
            name={{@name}}
            placeholder={{@placeholder}}
            readonly={{@readonly}}
            required={{@required}}
            rows={{@rows}}
            value={{this.value}}
            {{didInsert this.setInput}}
            {{didUpdate this.setInput}}
            {{on "blur" this.onBlur}}
            {{on "focusin" this.onFocusIn}}
            {{on "focusout" this.onFocusOut}}
            {{on "input" this.onInput}}
            {{on "keydown" this.onKeyDown}}
            {{on "keyup" this.onKeyUp}}
            {{this.focusElement}}
            ...attributes
          />
        {{else}}
          <input
            autocomplete={{@autocomplete}}
            autofocus={{if @autofocus "true"}}
            class={{if
              @isSmall
              "prism-input__input--small text-200--regular"
              "text-300--regular"
            }}
            disabled={{@disabled}}
            inputmode={{@inputmode}}
            name={{@name}}
            placeholder={{@placeholder}}
            readonly={{@readonly}}
            required={{@required}}
            type={{this.type}}
            value={{this.value}}
            {{didInsert this.setInput}}
            {{didUpdate this.setInput}}
            {{on "blur" this.onBlur}}
            {{on "focusin" this.onFocusIn}}
            {{on "focusout" this.onFocusOut}}
            {{on "input" this.onInput}}
            {{on "keydown" this.onKeyDown}}
            {{on "keyup" this.onKeyUp}}
            {{this.focusElement}}
            ...attributes
          />
        {{/if}}
        {{#if this.showContentOptions}}
          <div class="prism-input__content-options">
            {{#if @inlineLabel}}
              <span
                class="prism-input__inline-label text-200--regular"
              >{{@inlineLabel}}</span>
            {{/if}}
            {{#if this.showClearButton}}
              <span
                class="prism-input__button-clear"
                role="button"
                {{on "click" this.clearInput}}
              >
                <PrismIcon @name="cross" @size="small" />
              </span>
            {{/if}}
            {{#if @maxLength}}
              <span class="prism-input__indicators__counter text-200--regular">
                {{concat this.value.length "/" @maxLength}}
              </span>
            {{/if}}
            {{#if this.iconAfter}}
              {{#if this.iconAfter.onClick}}
                <span
                  class="prism-input__button-icon-after prism-input__button-icon-after--clickable"
                  role="button"
                  {{on "click" this.iconAfter.onClick}}
                >
                  <PrismIcon @name={{this.iconAfter.name}} @size="small" />
                </span>
              {{else}}
                <span class="prism-input__button-icon-after">
                  <PrismIcon @name={{this.iconAfter.name}} @size="small" />
                </span>
              {{/if}}
            {{/if}}
          </div>
        {{/if}}
      </div>

      {{#if @helpText}}
        <p class="prism-input__help-text text-200--regular">{{@helpText}}</p>
      {{/if}}
      {{#if this.hasErrorMessage}}
        <p class="prism-input__error-message text-200--regular">
          <PrismIcon
            @color="critical"
            @name="circle-error-filled"
            @size="small"
          />

          {{#if this.showRequiredMessage}}
            {{@requiredErrorMessage}}
          {{else}}
            {{@errorMessage}}
          {{/if}}
        </p>
      {{else if this.hasWarningMessage}}
        <p class="prism-input__warning-message text-200--regular">
          <PrismIcon @color="warning" @name="warning" @size="small" />
          {{@warningMessage}}
        </p>
      {{/if}}
    </div>
  </template>
}
